import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { Quote } from "lucide-react";

const testimonials = [
  { name: "Sarah Chen",    role: "CTO, MedInsight",    company: "Healthcare",    avatar: "SC",
    quote: "TekJovi built a diagnostic AI that cut our radiology review time by 40%. The domain expertise and technical rigour were exceptional — truly production-grade AI." },
  { name: "Marcus Reed",   role: "VP Engineering, FinCore", company: "Finance",
    avatar: "MR", quote: "Their fraud detection model slashed our false-positive rate by 60% while maintaining 99.3% true-positive accuracy. Delivered exactly what they promised, on time." },
  { name: "Priya Nair",    role: "Head of Data, RetailX",   company: "Retail",
    avatar: "PN", quote: "The recommendation engine contributed to a 32% uplift in average order value within three months. The integration was seamless and support outstanding." },
  { name: "David Kowalski",role: "COO, AutoManufact",  company: "Manufacturing",
    avatar: "DK", quote: "Predictive maintenance has saved us countless hours of unplanned downtime. TekJovi understood our OT environment and delivered a factory-floor solution." },
  { name: "Amara Osei",    role: "Director, EduPath",  company: "Education",
    avatar: "AO", quote: "Students using our TekJovi-powered adaptive learning platform show 2× the progress compared to our legacy system. The AI genuinely personalises at scale." },
  { name: "Lena Fischer",  role: "CTO, SwiftRoute",    company: "Logistics",
    avatar: "LF", quote: "Route optimisation cut our fuel costs by 18% in the first quarter. The team iterated rapidly, listened closely, and delivered lasting business value." },
];

function Card({ t }) {
  return (
    <div className="flex-shrink-0 w-80 card-flat rounded-2xl p-6 mx-3 flex flex-col gap-4 h-full">
      <Quote size={18} className="text-ink-300" />
      <p className="text-ink-700 text-sm leading-relaxed flex-1">"{t.quote}"</p>
      <div className="flex items-center gap-3 pt-3 border-t hairline">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-ink shrink-0 bg-lime">
          {t.avatar}
        </div>
        <div>
          <div className="text-ink text-sm font-semibold">{t.name}</div>
          <div className="text-ink-400 text-xs">{t.role}</div>
        </div>
        <span className="ml-auto text-xs px-2.5 py-0.5 rounded-full font-medium shrink-0 text-ink-500 bg-ink/4 border border-ink/10">
          {t.company}
        </span>
      </div>
    </div>
  );
}

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
      className={className}>
      {children}
    </motion.div>
  );
}

const doubled = [...testimonials, ...testimonials];

export default function Testimonials() {
  return (
    <section id="testimonials" className="relative py-24 bg-paper overflow-hidden">
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <FadeUp className="mb-10">
            <span className="eyebrow">05 — Client stories</span>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[0.98] tracking-[-0.03em] text-ink max-w-2xl">
              Trusted by teams that demand results.
            </h2>
          </FadeUp>
        </div>

        {/* Row 1 */}
        <FadeUp delay={0.15}>
          <div className="relative overflow-hidden mb-4">
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
              style={{ background: "linear-gradient(90deg, #F1F0EB, transparent)" }} />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
              style={{ background: "linear-gradient(-90deg, #F1F0EB, transparent)" }} />
            <div className="flex animate-marquee" style={{ width: "max-content" }}>
              {[...doubled.slice(0,6), ...doubled.slice(0,6)].map((t, i) => <Card key={`a${i}`} t={t} />)}
            </div>
          </div>
        </FadeUp>

        {/* Row 2 — reversed */}
        <FadeUp delay={0.2}>
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
              style={{ background: "linear-gradient(90deg, #F1F0EB, transparent)" }} />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
              style={{ background: "linear-gradient(-90deg, #F1F0EB, transparent)" }} />
            <div className="flex" style={{ width: "max-content", animation: "marquee 42s linear infinite reverse" }}>
              {[...doubled.slice(3,9), ...doubled.slice(3,9)].map((t, i) => <Card key={`b${i}`} t={t} />)}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
